import React, { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBolt,
    faCheck,
    faClock,
    faStar,
    faTriangleExclamation,
    faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { formatPoints, useTicketRecordedActiveSeconds, useTicketRewardContext, useTicketRewardEntries } from "./rewardClient";
import "./Rewards.css";

const SPEED_THRESHOLD_SECONDS = 600;

const LABELS = {
    task_completed: "Completion",
    speed_bonus: "Speed & quality",
    difficult_task: "Difficult task",
    urgent_management_request: "Urgent request",
    avoidable_rework: "Avoidable rework",
    manual_adjustment: "Manual adjustment",
    overdue_daily_penalty: "Overdue daily deduction",
};

const ICONS = {
    task_completed: faCheck,
    speed_bonus: faClock,
    difficult_task: faStar,
    urgent_management_request: faBolt,
    avoidable_rework: faTriangleExclamation,
    overdue_daily_penalty: faTriangleExclamation,
};

const AUTO_CONFIRM_TYPES = new Set(["speed_bonus"]);

const statusIcon = (status) => {
    if (["revoked", "rejected"].includes(status)) return faXmark;
    if (status === "pending") return faClock;
    return faCheck;
};

const compactDuration = (seconds) => {
    const value = Math.max(0, Number(seconds || 0));
    const minutes = Math.floor(value / 60);
    const secs = value % 60;
    return minutes ? `${minutes}m ${String(secs).padStart(2, "0")}s` : `${secs}s`;
};

const TicketRewardIndicator = ({ ticket, userId, showPotential = true }) => {
    const entries = useTicketRewardEntries(ticket?.id, userId);
    const { data: context } = useTicketRewardContext(ticket?.id, Boolean(ticket?.id && userId));
    const recordedActiveSeconds = useTicketRecordedActiveSeconds(ticket, userId);
    const contextMatchesCurrentAssignment = !context?.currentAssigneeId
        || context.currentAssigneeId === userId;
    const flags = contextMatchesCurrentAssignment ? (context?.flags || {}) : {};
    const currentWorkUnitId = contextMatchesCurrentAssignment ? (context?.workUnitId || null) : null;
    const speedWindowExceeded = recordedActiveSeconds > SPEED_THRESHOLD_SECONDS;
    const potential = 1
        + (speedWindowExceeded ? 0 : 1)
        + (flags?.difficult?.enabled ? 5 : 0)
        + (flags?.urgent_management_request?.enabled ? 10 : 0);

    const currentEntries = useMemo(() => {
        if (!currentWorkUnitId) return entries;
        return entries.filter((entry) => entry.work_unit_id === currentWorkUnitId);
    }, [entries, currentWorkUnitId]);

    const speedEntryExists = currentEntries.some((entry) => entry.type === "speed_bonus");
    const recordedPoints = currentEntries
        .filter((entry) => ["confirmed", "pending"].includes(entry.status))
        .reduce((sum, entry) => sum + Number(entry.points || 0), 0);
    const showMissedSpeedOutcome = currentEntries.length > 0 && speedWindowExceeded && !speedEntryExists;

    return (
        <section className="rw-ticket-reward" aria-label="Ticket rewards">
            <div className="rw-ticket-reward__heading">
                <div>
                    <span className="rw-eyebrow">Rewards</span>
                    <strong>{currentEntries.length ? "Reward outcome" : "Potential reward"}</strong>
                </div>
                {showPotential && (
                    <span className={`rw-ticket-reward__potential${speedWindowExceeded && !currentEntries.length ? " is-reduced" : ""}`}>
                        {currentEntries.length ? `Recorded +${recordedPoints} pts` : `Up to +${potential} pts`}
                    </span>
                )}
            </div>

            {currentEntries.length > 0 ? (
                <div className="rw-ticket-reward__rows">
                    {currentEntries.map((entry) => (
                        <div className={`rw-ticket-reward__row is-${entry.status}`} key={entry.id}>
                            <span className="rw-ticket-reward__row-icon">
                                <FontAwesomeIcon icon={statusIcon(entry.status)} />
                            </span>
                            <span className="rw-ticket-reward__row-copy">
                                <strong>{LABELS[entry.type] || entry.title || entry.type}</strong>
                                <small>
                                    {entry.status === "pending" && (AUTO_CONFIRM_TYPES.has(entry.type) ? "Confirming automatically…" : "Awaiting management review")}
                                    {entry.status === "confirmed" && "Confirmed"}
                                    {entry.status === "rejected" && (entry.rejection_reason || "Rejected during management review")}
                                    {entry.status === "revoked" && (entry.revoked_reason || "Removed after an audited correction")}
                                </small>
                            </span>
                            <b>{formatPoints(entry.points, { sign: true })}</b>
                        </div>
                    ))}
                    {showMissedSpeedOutcome && (
                        <div className="rw-ticket-reward__row is-ineligible">
                            <span className="rw-ticket-reward__row-icon"><FontAwesomeIcon icon={faXmark} /></span>
                            <span className="rw-ticket-reward__row-copy"><strong>Speed & quality</strong><small>10-minute window exceeded · {compactDuration(recordedActiveSeconds)} recorded</small></span>
                            <b>Not eligible</b>
                        </div>
                    )}
                </div>
            ) : (
                <div className="rw-ticket-reward__rows">
                    <div className="rw-ticket-reward__row is-potential">
                        <span className="rw-ticket-reward__row-icon"><FontAwesomeIcon icon={ICONS.task_completed} /></span>
                        <span className="rw-ticket-reward__row-copy"><strong>Completion</strong><small>When this assignment is completed</small></span>
                        <b>+1</b>
                    </div>
                    <div className={`rw-ticket-reward__row is-potential${speedWindowExceeded ? " is-ineligible" : ""}`}>
                        <span className="rw-ticket-reward__row-icon"><FontAwesomeIcon icon={speedWindowExceeded ? faXmark : ICONS.speed_bonus} /></span>
                        <span className="rw-ticket-reward__row-copy">
                            <strong>Speed & quality</strong>
                            <small>{speedWindowExceeded ? `10-minute window exceeded · ${compactDuration(recordedActiveSeconds)} recorded` : "Automatic +1 when recorded active work is within 10 minutes"}</small>
                        </span>
                        <b>{speedWindowExceeded ? "Not eligible" : "+1"}</b>
                    </div>
                    {flags?.difficult?.enabled && (
                        <div className="rw-ticket-reward__row is-potential">
                            <span className="rw-ticket-reward__row-icon"><FontAwesomeIcon icon={ICONS.difficult_task} /></span>
                            <span className="rw-ticket-reward__row-copy"><strong>Difficult task</strong><small>Classified for this work · management review required</small></span>
                            <b>+5</b>
                        </div>
                    )}
                    {flags?.urgent_management_request?.enabled && (
                        <div className="rw-ticket-reward__row is-potential">
                            <span className="rw-ticket-reward__row-icon"><FontAwesomeIcon icon={ICONS.urgent_management_request} /></span>
                            <span className="rw-ticket-reward__row-copy"><strong>Urgent request</strong><small>Classified for this work · management review required</small></span>
                            <b>+10</b>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
};

export default TicketRewardIndicator;
